import React, { useRef, useEffect, useMemo } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ReadonlyBlock {
  type: "readonly";
  standaloneHtml: string;
}

export interface EditableBlock {
  type: "editable";
  fieldName: string;
  defaultContent: string;
}

export interface EditableGroupBlock {
  type: "editable-group";
  fields: Array<{ fieldName: string; defaultContent: string }>;
}

export type TemplateBlock = ReadonlyBlock | EditableBlock | EditableGroupBlock;
export type ParsedEmailTemplate = TemplateBlock[];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Given Quill HTML output and the target element's tag name, return the
 * content that should be set as innerHTML on that element.
 *
 * For block-level elements that cannot nest other blocks (p, h1-h6, li…),
 * strip the outer block wrappers so we don't end up with <p><p>…</p></p>.
 */
const BLOCK_SINGLES = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6", "li", "dt", "dd"]);

/** Converts Quill's alignment CSS classes to inline text-align styles so the
 *  assembled email renders correctly without Quill's stylesheet. */
export function resolveQuillAlignments(html: string): string {
  if (!html) return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const map: Record<string, string> = {
    "ql-align-center": "center",
    "ql-align-right": "right",
    "ql-align-justify": "justify",
    "ql-align-left": "left",
  };
  Object.entries(map).forEach(([cls, align]) => {
    tmp.querySelectorAll<HTMLElement>(`.${cls}`).forEach((el) => {
      el.style.textAlign = align;
      el.classList.remove(cls);
      if (!el.className) el.removeAttribute("class");
    });
  });
  return tmp.innerHTML;
}

/**
 * Flattens a <td> innerHTML that contains a nested <table> (e.g. 3-column layout)
 * into a single Quill-compatible string.
 * Non-table siblings (title <p> etc.) come first, then each cell's innerHTML,
 * all joined by plain <hr> separators so Quill shows them as section dividers.
 */
export function flattenEditableContent(tdInnerHtml: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = tdInnerHtml;
  const table = tmp.querySelector("table");
  if (!table) return tdInnerHtml;

  const parts: string[] = [];
  Array.from(tmp.childNodes).forEach((node) => {
    if (node instanceof Element && node.tagName === "TABLE") return;
    const html =
      node instanceof Element
        ? node.outerHTML
        : (node.textContent?.trim() ?? "");
    if (html.trim()) parts.push(html);
  });
  table.querySelectorAll<HTMLElement>("tr > td").forEach((cell) => {
    parts.push(cell.innerHTML);
  });
  return parts.join("<hr>");
}

/**
 * Reconstructs a <td> innerHTML (with nested table) from flat Quill output
 * that was produced by flattenEditableContent.  Parts are split by <hr>.
 * Falls back to flat HTML if the part count no longer matches the template.
 */
export function reconstructEditableContent(
  flatHtml: string,
  originalTdInnerHtml: string,
): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = originalTdInnerHtml;
  const table = tmp.querySelector("table");
  if (!table) return flatHtml;

  const parts = flatHtml.split(/<hr\s*\/?>/i);
  const nonTableEls = Array.from(tmp.children).filter(
    (c) => c.tagName !== "TABLE",
  ) as HTMLElement[];
  const cells = Array.from(
    table.querySelectorAll<HTMLElement>("tr > td"),
  );

  if (parts.length !== nonTableEls.length + cells.length) return flatHtml;

  let pi = 0;
  nonTableEls.forEach((el) => {
    el.innerHTML = quillHtmlToInner(
      resolveQuillAlignments(parts[pi++] ?? ""),
      el.tagName,
    );
  });
  cells.forEach((cell) => {
    cell.innerHTML = resolveQuillAlignments(parts[pi++] ?? "");
  });
  return tmp.innerHTML;
}

export function quillHtmlToInner(userHtml: string, targetTag: string): string {
  if (!BLOCK_SINGLES.has(targetTag.toLowerCase())) return userHtml;
  const tmp = document.createElement("div");
  tmp.innerHTML = userHtml;
  const parts: string[] = [];
  tmp.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6").forEach((el) => {
    parts.push(el.innerHTML);
  });
  return parts.join("<br>") || tmp.innerHTML || userHtml;
}

// ─── Parser ───────────────────────────────────────────────────────────────────

export function parseEmailTemplate(rawHtml: string): ParsedEmailTemplate | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");

    if (!doc.querySelector('[data-section="editable"]')) return null;

    const headHtml = doc.head.outerHTML;

    // Prefer a <tr> with data-section to locate the main table; avoids
    // mistakenly landing on a nested element (e.g. <p data-section="editable">)
    // whose closest("table") would be an inner table, not the outer email table.
    const firstSectioned =
      doc.querySelector("tr[data-section]") ??
      doc.querySelector("[data-section]");
    const innerTable = firstSectioned?.closest("table");
    if (!innerTable) return null;

    const innerTableAttrs = Array.from(innerTable.attributes)
      .map((a) => `${a.name}="${a.value}"`)
      .join(" ");

    const tableBody =
      innerTable.querySelector("tbody") ?? (innerTable as HTMLTableElement);
    const allRows = Array.from(tableBody.children) as Element[];

    const buildReadonlyHtml = (
      rows: Element[],
      isFirst: boolean,
      isLast: boolean,
    ) => {
      const pt = isFirst ? "30px" : "0";
      const pb = isLast ? "30px" : "0";
      return `<!DOCTYPE html><html>${headHtml}<body style="margin:0;padding:0;background-color:#e8ecf0;"><table width="100%" cellpadding="0" cellspacing="0" style="background-color:#e8ecf0;"><tr><td align="center" style="padding:${pt} 10px ${pb};"><table ${innerTableAttrs}>${rows.map((r) => r.outerHTML).join("")}</table></td></tr></table></body></html>`;
    };

    const blocks: ParsedEmailTemplate = [];
    let readonlyBuffer: Element[] = [];

    const flushReadonly = (isLast = false) => {
      if (!readonlyBuffer.length) return;
      const isFirst = blocks.length === 0;
      blocks.push({
        type: "readonly",
        standaloneHtml: buildReadonlyHtml(readonlyBuffer, isFirst, isLast),
      });
      readonlyBuffer = [];
    };

    for (const row of allRows) {
      if (row.getAttribute("data-section") === "editable") {
        // ── Row-level editable ──
        flushReadonly(false);
        const td = row.querySelector("td");
        const rawContent = td?.innerHTML ?? "";
        blocks.push({
          type: "editable",
          fieldName: row.getAttribute("data-field") ?? "Message",
          // Flatten nested table HTML → clean Quill-compatible content with <hr> separators
          defaultContent: flattenEditableContent(rawContent),
        });
      } else {
        // ── Check for nested editable elements inside a readonly row ──
        const nestedEditables = Array.from(
          row.querySelectorAll<HTMLElement>('[data-section="editable"]'),
        );
        if (nestedEditables.length > 0) {
          // Strip all editable elements from the clone so the iframe renders the
          // surrounding chrome (table borders, column headers, etc.) cleanly.
          const clonedRow = row.cloneNode(true) as Element;
          clonedRow
            .querySelectorAll('[data-section="editable"]')
            .forEach((el) => el.remove());
          readonlyBuffer.push(clonedRow);
          flushReadonly(false);

          if (nestedEditables.length === 1) {
            const el = nestedEditables[0];
            blocks.push({
              type: "editable",
              fieldName: el.getAttribute("data-field") ?? "Message",
              defaultContent: el.innerHTML,
            });
          } else {
            // Multiple nested editables → render side-by-side to mirror column layout
            blocks.push({
              type: "editable-group",
              fields: nestedEditables.map((el) => ({
                fieldName: el.getAttribute("data-field") ?? "Message",
                defaultContent: el.innerHTML,
              })),
            });
          }
        } else {
          readonlyBuffer.push(row);
        }
      }
    }

    flushReadonly(true);

    return blocks.length ? blocks : null;
  } catch {
    return null;
  }
}

// ─── ReadonlySection ──────────────────────────────────────────────────────────

const ReadonlySection: React.FC<{ srcDoc: string; imageUrl?: string }> = ({
  srcDoc,
  imageUrl,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resolvedSrcDoc = useMemo(() => {
    if (!imageUrl) return srcDoc;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(srcDoc, "text/html");

      // 1. Replace banner-image src (handles both class= and className= attributes)
      doc.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
        const cls =
          img.getAttribute("class") ?? img.getAttribute("className") ?? "";
        if (cls.includes("banner-image")) img.src = imageUrl;
      });

      // 2. Remove the gradient overlay div (position:absolute + linear-gradient)
      doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
        const s = (div.getAttribute("style") ?? "").replace(/\s/g, "");
        if (s.includes("position:absolute") && s.includes("linear-gradient")) {
          div.remove();
        }
      });

      // 3. Remove the banner-text div
      doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
        if ((div.getAttribute("class") ?? "").includes("banner-text")) {
          div.remove();
        }
      });

      return "<!DOCTYPE html>" + doc.documentElement.outerHTML;
    } catch {
      return srcDoc;
    }
  }, [srcDoc, imageUrl]);

  const fit = () => {
    try {
      const h = iframeRef.current?.contentDocument?.body?.scrollHeight;
      if (h && iframeRef.current) iframeRef.current.style.height = `${h}px`;
    } catch {
      /* cross-origin guard */
    }
  };

  useEffect(() => {
    const t = setTimeout(fit, 350);
    return () => clearTimeout(t);
  }, [resolvedSrcDoc]);

  return (
    <div style={{ position: "relative" }}>
      <iframe
        ref={iframeRef}
        srcDoc={resolvedSrcDoc}
        title="readonly-section"
        style={{ width: "100%", border: "none", display: "block", minHeight: 40 }}
        onLoad={fit}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          cursor: "not-allowed",
          userSelect: "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            background: "rgba(0,0,0,0.38)",
            color: "#fff",
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 3,
            fontFamily: "Arial,sans-serif",
          }}
        >
          🔒 Read-only
        </span>
      </div>
    </div>
  );
};

// ─── EmailTemplateEditor ──────────────────────────────────────────────────────

interface EmailTemplateEditorProps {
  template: ParsedEmailTemplate;
  renderEditable: (fieldName: string, defaultContent: string) => React.ReactNode;
  imageUrl?: string;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  template,
  renderEditable,
  imageUrl,
}) => (
  <div
    style={{
      border: "1px solid #e8ecf0",
      borderRadius: 8,
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    }}
  >
    {template.map((block, idx) => {
      if (block.type === "readonly") {
        return <ReadonlySection key={idx} srcDoc={block.standaloneHtml} imageUrl={imageUrl} />;
      }

      if (block.type === "editable-group") {
        return (
          <div key={idx}>
            {/* Single shared header — looks like one editing block */}
            <div
              style={{
                background: "#e6f4ff",
                borderTop: "2px dashed #1677ff",
                borderBottom: "1px solid #91caff",
                padding: "5px 14px",
                fontSize: 12,
                color: "#1677ff",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              ✏️ {block.fields.map((f) => f.fieldName).join(" / ")} — Editable
            </div>

            {/* Column labels */}
            <div style={{ display: "flex", background: "#f8fbff", borderBottom: "1px solid #e8ecf0" }}>
              {block.fields.map(({ fieldName }, colIdx) => (
                <div
                  key={fieldName}
                  style={{
                    flex: 1,
                    padding: "4px 12px",
                    fontSize: 11,
                    color: "#888",
                    borderRight:
                      colIdx < block.fields.length - 1
                        ? "1px solid #e8ecf0"
                        : undefined,
                  }}
                >
                  {fieldName}
                </div>
              ))}
            </div>

            {/* Editors side-by-side */}
            <div style={{ display: "flex", background: "#fff" }}>
              {block.fields.map(({ fieldName, defaultContent }, colIdx) => (
                <div
                  key={fieldName}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRight:
                      colIdx < block.fields.length - 1
                        ? "1px solid #e8ecf0"
                        : undefined,
                  }}
                >
                  {renderEditable(fieldName, defaultContent)}
                </div>
              ))}
            </div>

            <div style={{ borderBottom: "2px dashed #1677ff" }} />
          </div>
        );
      }

      return (
        <div key={idx}>
          <div
            style={{
              background: "#e6f4ff",
              borderTop: "2px dashed #1677ff",
              borderBottom: "1px solid #91caff",
              padding: "5px 14px",
              fontSize: 12,
              color: "#1677ff",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            ✏️ {block.fieldName} — Editable
          </div>
          <div style={{ background: "#fff" }}>
            {renderEditable(block.fieldName, block.defaultContent)}
          </div>
          <div style={{ borderBottom: "2px dashed #1677ff" }} />
        </div>
      );
    })}
  </div>
);

export default EmailTemplateEditor;
